import { LogOut, UserCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { DEMO_PROFILES, mintTestToken } from '@/auth/demoProfiles';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Dev/demo-only TopBar control letting a presenter hop between role
 * profiles (scope sets) without leaving the app, plus sign out. Rendered by
 * `TopBar` only when `isDemoAuthEnabled()` is true. Never ships active in a
 * normal production build.
 */
export function DemoProfileSwitcher() {
  const { t } = useTranslation('auth');
  const { user, scopes, login, logout } = useAuth();

  if (!user) return null;

  const activeProfile = DEMO_PROFILES.find((profile) => {
    if (profile.scopes.length !== scopes.size) return false;
    return profile.scopes.every((scope) => scopes.has(scope));
  });
  const activeLabel = activeProfile ? t(activeProfile.labelKey) : t('demoProfiles.custom');

  function handleSelect(profileId: string, profileScopes: readonly string[]) {
    login(mintTestToken({ scopes: profileScopes, sub: `demo-${profileId}` }));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('demoProfiles.switcherLabel')}
          className="ml-1 flex items-center gap-2 rounded p-1 pl-2 text-xs text-muted-foreground border-l border-border hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <UserCircle2 size={14} />
          <span title={user.sub}>{activeLabel}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]">
        <DropdownMenuLabel>{t('demoProfiles.sectionHeading')}</DropdownMenuLabel>
        {DEMO_PROFILES.map((profile) => (
          <DropdownMenuItem
            key={profile.id}
            onSelect={() => handleSelect(profile.id, profile.scopes)}
          >
            <div className="flex flex-col">
              <span>{t(profile.labelKey)}</span>
              <span className="text-xs text-muted-foreground">{t(profile.descriptionKey)}</span>
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()}>
          <LogOut size={14} className="mr-2" />
          {t('userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
